import VaporToolbar from "@vapor/react-custom/VaporToolbar";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    Stack,
    TextareaAutosize,
    TextField,
    useTheme,
} from "@vapor/react-material";
import { useMemo, useState, type ChangeEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useAddActivity } from "../../../../../core/api/use-add-activity/useAddActivity";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";

export const MAX_NOTE_LENGTH = 1024;
export const MAX_TITLE_LENGTH = 200;

interface FulfilmentAddActivityDto {
    notes?: string;
    title: string;
}

interface AddActivityProps {
    levyId: string;
    onAddActivity: () => unknown;
    onClickCancel: () => unknown;
    taxPayerWorkspaceId: string;
    year: number;
}

export const AddActivity = ({
    levyId,
    onAddActivity,
    onClickCancel,
    taxPayerWorkspaceId,
    year,
}: AddActivityProps): ReactElement => {
    const { showTextMessage } = useSuccessNotification();
    const { t } = useTranslation();
    const theme = useTheme();

    const [data, setData] = useState<FulfilmentAddActivityDto>({
        title: t(
            "views.fulfilment_detail.components.add_activity.activity_name.placeholder",
        ),
        notes: undefined,
    });

    const isDataValid = useMemo(() => {
        return (
            data &&
            !!data.title &&
            data.title.length <= MAX_TITLE_LENGTH &&
            (!data.notes || data.notes.length <= MAX_NOTE_LENGTH)
        );
    }, [data]);

    const addActivityMutation = useAddActivity();

    const onClickConfirmButton = async () => {
        try {
            await addActivityMutation.mutateAsync({
                title: data.title,
                notes: data.notes,
                taxPayerWorkspaceId: taxPayerWorkspaceId,
                levyId: levyId,
                year: year,
            });
        } catch {
            return;
        }
        showTextMessage(
            t(
                "views.fulfilment_detail.components.add_activity.actions.create.successfull",
            ),
        );
        onAddActivity();
    };

    return (
        <Stack p={3}>
            <Stack gap={1} justifyContent="space-between">
                <Stack>
                    <TextField
                        disabled={addActivityMutation.isPending}
                        id="activity-textfield"
                        error={
                            !data.title || data.title.length > MAX_TITLE_LENGTH
                        }
                        fullWidth
                        label={t(
                            "views.fulfilment_detail.components.add_activity.activity_name.label",
                        )}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setData((prev) => {
                                return {
                                    ...prev,
                                    title: event.target.value,
                                };
                            });
                        }}
                        value={data.title}
                    />
                    {data &&
                        data.title &&
                        data.title.length > MAX_TITLE_LENGTH && (
                            <FormHelperText>
                                {t(
                                    "views.fulfilment_detail.components.add_activity.title.text_helper",
                                    { number: MAX_TITLE_LENGTH },
                                )}
                            </FormHelperText>
                        )}
                </Stack>
                <Stack>
                    <Stack gap={1}>
                        <Typography
                            variant={
                                addActivityMutation.isPending
                                    ? "formLabelDisabled"
                                    : "body500"
                            }
                        >
                            {t(
                                "views.fulfilment_detail.components.add_activity.note.label",
                            )}
                        </Typography>
                        <TextareaAutosize
                            disabled={addActivityMutation.isPending}
                            error={
                                !!data.notes &&
                                data.notes.length > MAX_NOTE_LENGTH
                            }
                            onChange={(event) => {
                                setData((prev) => {
                                    return {
                                        ...prev,
                                        notes: event.target.value,
                                    };
                                });
                            }}
                            minRows={3}
                            placeholder={t(
                                "views.fulfilment_detail.components.add_activity.note.placeholder",
                            )}
                            style={{
                                borderColor:
                                    !!data.notes &&
                                    data.notes.length > MAX_NOTE_LENGTH
                                        ? theme.palette.primary.accentRustyRed
                                        : undefined,
                            }}
                            value={data.notes}
                        />
                    </Stack>
                    {!!data.notes && data.notes.length > MAX_NOTE_LENGTH && (
                        <FormHelperText>
                            {t(
                                "views.fulfilment_detail.components.add_activity.note.text_helper",
                                { number: MAX_NOTE_LENGTH },
                            )}
                        </FormHelperText>
                    )}
                </Stack>
            </Stack>
            <Box
                sx={{
                    ml: -3,
                }}
            >
                <VaporToolbar
                    contentRight={[
                        <Button
                            disabled={
                                !isDataValid || addActivityMutation.isPending
                            }
                            onClick={onClickConfirmButton}
                            size="large"
                            variant="contained"
                        >
                            {t(
                                "views.fulfilment_detail.drawer.content.new_activity.buttons.submit",
                            )}
                            {addActivityMutation.isPending && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>,
                    ]}
                    contentLeft={[
                        <Button
                            disabled={addActivityMutation.isPending}
                            onClick={onClickCancel}
                            size="large"
                            variant="outlined"
                        >
                            {t(
                                "views.fulfilment_detail.drawer.content.new_activity.buttons.cancel",
                            )}
                        </Button>,
                    ]}
                    size="large"
                    variant="regular"
                />
            </Box>
        </Stack>
    );
};
